/* Booking confirmation — tenant-aware, post-submit summary screen.
   Rendered immediately after the booking is created. Receives the freshly
   inserted booking record via router state from useBookingFlow so we can
   show full session details + the studio's booking policy + calendar exports
   without needing an authenticated `GET /bookings/{ref}` endpoint (that one
   was retired in SEC-003).

   Design follows the user's reference shot: pale top band with check icon,
   thanks headline, studio name, optional hero image strip, then policy +
   session details + calendar buttons. Theming inherits the active template
   via CSS variables (--tenant-bg / --tenant-fg / --tenant-accent etc.) so
   the same component reads natively whether the visitor is on Editorial,
   Bold, Botanical, Brutalist, etc. */
import React, { useEffect, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Check, Calendar as CalIcon, Clock, MapPin, Sparkles } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";

/* ---------------- helpers ---------------- */

/** "Thursday, July 2, 2026" from YYYY-MM-DD. Returns the raw input on parse
 *  failures so we never render "Invalid Date" to a paying customer. */
function formatPrettyDate(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return "";
  const [y, m, d] = String(yyyy_mm_dd).split("-").map(Number);
  if (!y || !m || !d) return yyyy_mm_dd;
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return yyyy_mm_dd;
  return dt.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

/** "2:30 PM" from "14:30". */
function formatPrettyTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = String(hhmm).split(":").map(Number);
  if (h == null || m == null) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

/** Compose a UTC-like local time block "YYYYMMDDTHHMMSS" used in iCal/Google. */
function toIcsLocal(yyyy_mm_dd, hhmm, addMinutes = 0) {
  if (!yyyy_mm_dd || !hhmm) return "";
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  const [hh, mm] = hhmm.split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh, mm + addMinutes);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
}

/** Google Calendar template URL builder. Defaults to a 90-min block when we
 *  don't know the actual duration (tier model doesn't always include it). */
function googleCalendarUrl({ title, start, end, details, location }) {
  const u = new URL("https://calendar.google.com/calendar/render");
  u.searchParams.set("action", "TEMPLATE");
  u.searchParams.set("text", title);
  u.searchParams.set("dates", `${start}/${end}`);
  if (details) u.searchParams.set("details", details);
  if (location) u.searchParams.set("location", location);
  return u.toString();
}

/** Build an .ics blob URL synchronously. Caller revokes when done. */
function buildIcsBlobUrl({ title, start, end, details, location, uid }) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Lumetier//Booking//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${start}Z`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${title.replace(/\n/g, " ")}`,
    details ? `DESCRIPTION:${String(details).replace(/\n/g, "\\n")}` : "",
    location ? `LOCATION:${location}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  return URL.createObjectURL(blob);
}

/** Render the studio's free-text booking_policy with light structure:
 *  - blank line = paragraph break
 *  - short line (<= 60 chars) immediately followed by more text = heading
 *  This is intentionally forgiving — studios write their policy in plain
 *  text and we don't want to force a format on them. */
function PolicyBlock({ text }) {
  const blocks = useMemo(() => {
    if (!text) return [];
    return String(text).split(/\n{2,}/).map((para) => {
      const lines = para.split(/\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) return null;
      const first = lines[0];
      const isHeading = first.length <= 60 && lines.length > 1 && !first.endsWith(".");
      return { heading: isHeading ? first : null, body: (isHeading ? lines.slice(1) : lines).join(" ") };
    }).filter(Boolean);
  }, [text]);

  if (blocks.length === 0) return null;
  return (
    <div className="space-y-6">
      {blocks.map((b, i) => (
        <div key={i}>
          {b.heading && (
            <h3 className="text-base font-semibold mb-1.5" style={{ color: "var(--tenant-fg)" }}>
              {b.heading}
            </h3>
          )}
          <p className="leading-relaxed text-sm sm:text-[15px]" style={{ color: "var(--tenant-fg-soft, rgba(0,0,0,0.72))" }}>
            {b.body}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- main page ---------------- */

export default function BookingConfirmation() {
  const { reference } = useParams();
  const location = useLocation();
  const { settings } = useSiteSettings();

  const studioName = settings?.studio_name || "the Studio";
  const studioLocation = settings?.contact_location || settings?.studio_location || "";
  const policyText = settings?.booking_policy || "";

  // Booking + the service's own hero image handed to us via router state by
  // useBookingFlow.submit(). If a customer reloads this page (state lost), we
  // degrade gracefully — we still show the reference + a polite "check your
  // email" message and the studio's policy, just without the per-booking
  // session details or the service hero band.
  const booking = location.state?.booking || null;
  const heroImage = location.state?.serviceImage || null;
  const hasDetails = Boolean(booking);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Booking received — ${studioName}`;
  }, [studioName]);

  // Build calendar payload only when we have booking details (post-submit).
  const calendar = useMemo(() => {
    if (!booking) return null;
    const title = `${booking.service_name} — ${studioName}`;
    const start = toIcsLocal(booking.session_date, booking.session_time, 0);
    const end = toIcsLocal(booking.session_date, booking.session_time, 90);
    const details = [
      `Reference: ${booking.reference}`,
      booking.tier_name ? `Tier: ${booking.tier_name}` : "",
      booking.tier_price ? `Total: $${booking.tier_price}` : "",
    ].filter(Boolean).join("\\n");
    const loc = booking.location || studioLocation || "";
    return {
      google: googleCalendarUrl({ title, start, end, details, location: loc }),
      icsHref: buildIcsBlobUrl({ title, start, end, details, location: loc, uid: `${booking.reference}@lumetier` }),
      icsFilename: `${booking.reference}.ics`,
    };
  }, [booking, studioName, studioLocation]);

  return (
    <div
      className="min-h-screen"
      data-testid="booking-confirmation"
      style={{
        background: "var(--tenant-bg, #FFFFFF)",
        color: "var(--tenant-fg, #0E0D0C)",
        fontFamily: "var(--tenant-sans, system-ui, sans-serif)",
      }}
    >
      {/* Soft top band with check icon */}
      <header
        className="px-6 py-14 sm:py-20 text-center"
        style={{ background: "var(--tenant-muted, rgba(0,0,0,0.04))" }}
      >
        <div
          className="mx-auto w-16 h-16 rounded-full grid place-items-center mb-6 ring-1"
          style={{
            background: "transparent",
            borderColor: "var(--tenant-fg-line, rgba(0,0,0,0.15))",
          }}
          aria-hidden
        >
          <Check className="w-7 h-7" style={{ color: "var(--tenant-fg)" }} />
        </div>
        <h1
          className="text-4xl sm:text-5xl tracking-tight"
          style={{ fontFamily: "var(--tenant-serif, var(--tenant-sans))", fontWeight: 600 }}
        >
          Thanks for booking!
        </h1>
        <p className="mt-4 text-base sm:text-lg" style={{ color: "var(--tenant-fg-soft, rgba(0,0,0,0.65))" }}>
          Your session with <strong style={{ color: "var(--tenant-fg)" }}>{studioName}</strong> will be confirmed shortly &mdash; a confirmation email is on its way.
        </p>
        <p
          className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] px-3 py-1.5 rounded-full"
          style={{
            color: "var(--tenant-accent)",
            background: "var(--tenant-bg, #FFFFFF)",
            border: "1px solid var(--tenant-fg-line, rgba(0,0,0,0.15))",
          }}
          data-testid="booking-reference"
        >
          <Sparkles className="w-3.5 h-3.5" /> Reference {reference || "—"}
        </p>
      </header>

      {/* Hero image strip (if studio has uploaded one) */}
      {heroImage && (
        <div
          className="w-full"
          style={{
            aspectRatio: "16 / 7",
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
      )}

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <p className="text-base sm:text-lg leading-relaxed mb-10">
          Your session has been successfully booked. We&rsquo;ll review the request and send a confirmation email within one business day.
        </p>

        {/* Booking policy */}
        {policyText && (
          <section className="mb-12" data-testid="booking-policy-block">
            <div
              className="text-[10px] uppercase tracking-[0.42em] mb-5"
              style={{ color: "var(--tenant-accent)" }}
            >
              Please note
            </div>
            <PolicyBlock text={policyText} />
          </section>
        )}

        {/* Session details */}
        {hasDetails && (
          <section className="mb-12" data-testid="booking-details-block">
            <h2 className="text-2xl mb-5" style={{ fontFamily: "var(--tenant-serif, var(--tenant-sans))", fontWeight: 600 }}>
              Session Details
            </h2>
            <dl className="space-y-4 text-sm sm:text-[15px]">
              <Row icon={<CalIcon className="w-4 h-4" />}>
                <strong>{booking.service_name}</strong>
                {booking.tier_name ? ` (${booking.tier_name})` : ""}
                {booking.tier_price ? ` — $${booking.tier_price}` : ""}
              </Row>
              <Row icon={<CalIcon className="w-4 h-4" />}>{formatPrettyDate(booking.session_date)}</Row>
              <Row icon={<Clock className="w-4 h-4" />}>
                {formatPrettyTime(booking.session_time)}
                {settings?.timezone ? ` (${settings.timezone})` : ""}
              </Row>
              {(booking.location || studioLocation) && (
                <Row icon={<MapPin className="w-4 h-4" />}>{booking.location || studioLocation}</Row>
              )}
            </dl>
          </section>
        )}

        {/* Add to calendar */}
        {calendar && (
          <section data-testid="booking-calendar-block">
            <h2 className="text-2xl mb-4" style={{ fontFamily: "var(--tenant-serif, var(--tenant-sans))", fontWeight: 600 }}>
              Add to calendar
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <a
                href={calendar.google}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2.5 px-4 py-3.5 border text-sm transition-colors"
                style={{ borderColor: "var(--tenant-fg-line, rgba(0,0,0,0.18))", color: "var(--tenant-fg)" }}
                data-testid="cal-google"
              >
                <GoogleG className="w-4 h-4" /> Google Calendar
              </a>
              <a
                href={calendar.icsHref}
                download={calendar.icsFilename}
                className="flex items-center justify-center gap-2.5 px-4 py-3.5 border text-sm transition-colors"
                style={{ borderColor: "var(--tenant-fg-line, rgba(0,0,0,0.18))", color: "var(--tenant-fg)" }}
                data-testid="cal-ics"
              >
                <CalIcon className="w-4 h-4" /> iCal / Outlook
              </a>
            </div>
          </section>
        )}

        <div className="mt-14 text-center">
          <Link
            to={tenantHref("/")}
            className="text-sm underline underline-offset-4"
            style={{ color: "var(--tenant-fg-soft, rgba(0,0,0,0.55))" }}
            data-testid="return-home"
          >
            Return to {studioName}
          </Link>
        </div>
      </main>
    </div>
  );
}

/* ---------------- small bits ---------------- */

function Row({ icon, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0" style={{ color: "var(--tenant-fg-soft, rgba(0,0,0,0.5))" }} aria-hidden>
        {icon}
      </span>
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

function GoogleG(props) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path fill="#4285F4" d="M21.6 12.2c0-.7-.06-1.4-.18-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.24c1.9-1.75 3-4.32 3-7.3z"/>
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.24-2.5c-.9.6-2.06.95-3.38.95-2.6 0-4.8-1.76-5.6-4.12H3.07v2.6A10 10 0 0 0 12 22z"/>
      <path fill="#FBBC05" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.07a10 10 0 0 0 0 9l3.33-2.6z"/>
      <path fill="#EA4335" d="M12 6.07c1.46 0 2.78.5 3.82 1.49l2.86-2.86A10 10 0 0 0 3.07 7.5l3.33 2.6C7.2 7.84 9.4 6.07 12 6.07z"/>
    </svg>
  );
}
